import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { db } from '../../lib/db';

interface TaskHistoryModalProps {
  taskTemplateId: string;
  taskTitle: string;
  taskDescription?: string;
  taskFrequency?: string;
  taskPriority?: string;
  dbUserId: string;
  onClose: () => void;
}

function TaskHistoryModal({ taskTemplateId, taskTitle, taskDescription, taskFrequency, taskPriority, dbUserId, onClose }: TaskHistoryModalProps) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [taskTemplateId, taskTitle, taskDescription, taskFrequency, taskPriority]);

  const loadHistory = async () => {
    try {
      // If we have task properties, use them to find all matching history
      let historyData;
      if (taskFrequency && taskPriority) {
        historyData = await db.regularTaskHistory.getHistoryByTaskProperties(
          taskTitle,
          taskDescription || null,
          taskFrequency,
          taskPriority,
          dbUserId,
          90
        );
      } else {
        // Fallback to template ID
        historyData = await db.regularTaskHistory.getHistoryWithTimeEntries(taskTemplateId, dbUserId, 90);
      }
      setHistory(historyData);
    } catch (error) {
      console.error('Error loading task history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getCompletionStats = () => {
    // Get all completed entries
    const completedEntries = history.filter(h => h.completed_date);
    const completionCount = completedEntries.length;
    
    // Get entries with time tracked
    const entriesWithTime = completedEntries.filter(h => h.total_minutes > 0);
    
    if (completionCount === 0) {
      return { 
        completionCount: 0, 
        avgTime: 0, 
        minTime: 0, 
        maxTime: 0,
        totalTime: 0,
        entriesWithTime: 0
      };
    }
    
    if (entriesWithTime.length === 0) {
      return { 
        completionCount, 
        avgTime: 0, 
        minTime: 0, 
        maxTime: 0,
        totalTime: 0,
        entriesWithTime: 0
      };
    }
    
    // Calculate time statistics from entries with time tracked
    const times = entriesWithTime.map(h => h.total_minutes);
    const totalTime = times.reduce((sum, time) => sum + time, 0);
    const avgTime = totalTime / entriesWithTime.length;
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    
    return { 
      completionCount, 
      avgTime, 
      minTime, 
      maxTime,
      totalTime,
      entriesWithTime: entriesWithTime.length
    };
  };

  const stats = getCompletionStats();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Task History: {taskTitle}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Stats Summary */}
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Completions</div>
              <div className="text-lg font-semibold text-gray-900">
                {stats.completionCount}
              </div>
              {stats.entriesWithTime < stats.completionCount && (
                <div className="text-xs text-gray-500 mt-1">
                  {stats.entriesWithTime} with time
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Avg. Time</div>
              <div className="text-lg font-semibold text-gray-900">
                {stats.entriesWithTime > 0 ? formatDuration(stats.avgTime) : 'No time data'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Min Time</div>
              <div className="text-lg font-semibold text-gray-900">
                {stats.entriesWithTime > 0 ? formatDuration(stats.minTime) : '-'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Max Time</div>
              <div className="text-lg font-semibold text-gray-900">
                {stats.entriesWithTime > 0 ? formatDuration(stats.maxTime) : '-'}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-200px)]">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No history yet</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {history.map((entry) => (
                <div key={entry.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {format(new Date(entry.generated_date), 'EEEE, MMMM d, yyyy')}
                        </span>
                        {entry.completed_date && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </span>
                        )}
                      </div>
                      
                      {entry.time_entries && entry.time_entries.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {entry.time_entries.map((timeEntry: any) => (
                            <div key={timeEntry.id} className="flex items-center space-x-2 text-sm text-gray-600">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span>
                                {format(new Date(timeEntry.start_time), 'h:mm a')} - 
                                {timeEntry.end_time ? format(new Date(timeEntry.end_time), 'h:mm a') : 'In progress'}
                              </span>
                              <span className="text-gray-500">
                                ({formatDuration(timeEntry.duration_minutes || 0)})
                              </span>
                              {timeEntry.notes && (
                                <span className="text-gray-500 italic">- {timeEntry.notes}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4 text-right">
                      {entry.total_minutes > 0 && (
                        <div className="text-sm font-medium text-gray-900">
                          {formatDuration(entry.total_minutes)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskHistoryModal;